import { useState } from 'react';
import {
  api,
  type ReceivablesReport, type ReceivablesRow,
  type ReceivablesInvoice, type ReceivablesCredit,
} from '../lib/api';
import { fmtMonth } from '../lib/fmt';
import { downloadCsv } from '../lib/csv';
import {
  ActionButton, Card, CardHeader, EmptyState, MetricCard, SectionLabel,
  SortableTh, Spinner, Table, Td, Th, Tr,
} from '../components/ui';
import { useTableSort } from '../hooks/useTableSort';
import { InvoiceLink } from '../components/InvoiceLink';

/**
 * Receivables — expected cash collection per customer for a chosen month.
 * Reads our locally-synced chargeover_invoices + chargeover_transactions
 * tables (refresh syncs them on every run), so a single click returns
 * results without round-tripping ChargeOver.
 *
 * Numbers are as fresh as the last refresh wrote; the billing-page
 * sync banner is the canonical freshness indicator. Receivables itself
 * doesn't gate on staleness — operators using this for collections
 * work would rather see slightly-stale data than be blocked.
 */

function fmtMoney(n: number): string {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}

function currentMonthKey(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function lastCompleteMonthKey(): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export default function AdminBillingReceivablesPage() {
  const [month, setMonth] = useState<string>(currentMonthKey());
  const [report, setReport] = useState<ReceivablesReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [allowedMonths, setAllowedMonths] = useState<string[] | null>(null);

  if (allowedMonths === null) {
    api.admin.billing.months()
      .then(({ months }) => setAllowedMonths(months))
      .catch(() => setAllowedMonths([currentMonthKey(), lastCompleteMonthKey()]));
  }

  async function run() {
    setLoading(true);
    setError(null);
    setExpanded(null);
    try {
      const data = await api.admin.billing.receivables(month);
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <SectionLabel>Receivables</SectionLabel>
      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Expected cash collection</span>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
            Per customer, for invoices due in the selected month. Reads local DB; freshness mirrors last refresh.
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              style={{
                padding: '4px 8px', fontSize: 12, borderRadius: 'var(--radius-sm)',
                border: '0.5px solid var(--color-border-strong)',
                background: 'var(--color-surface)', color: 'var(--color-text)',
              }}
            >
              {(allowedMonths ?? [month]).map((m) => (
                <option key={m} value={m}>{fmtMonth(m)}</option>
              ))}
            </select>
            <ActionButton onClick={run} disabled={loading} variant="primary">
              {loading ? 'Running…' : 'Run report'}
            </ActionButton>
            {report && (
              <ActionButton onClick={() => exportCsv(report)} disabled={report.rows.length === 0}>
                Export CSV
              </ActionButton>
            )}
          </span>
        </CardHeader>
        {error && (
          <div style={{
            padding: '10px 16px', background: 'var(--color-red-bg)',
            color: 'var(--color-red-text)', fontSize: 12, borderTop: '0.5px solid var(--color-border)',
          }}>
            Report failed: {error}
          </div>
        )}
      </Card>

      {!report && !loading && <EmptyState message="Pick a month and click Run report." />}
      {loading && <Spinner />}

      {report && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 10 }}>
            <MetricCard
              label="Customers"
              value={report.rows.length}
              sub={`Range: ${report.range.from} → ${report.range.to}`}
            />
            <MetricCard
              label="Total invoiced"
              value={fmtMoney(report.totals.total)}
              sub={`${report.rows.reduce((s, r) => s + r.invoiceCount, 0)} invoices due`}
            />
            <MetricCard
              label="Outstanding balance"
              value={fmtMoney(report.totals.balance)}
              accent={report.totals.balance > 0 ? 'amber' : undefined}
              sub={`After ${fmtMoney(report.totals.payments)} paid + ${fmtMoney(report.totals.credits)} credits`}
            />
            <MetricCard
              label="Unapplied credit"
              value={fmtMoney(report.totals.availableCredit)}
              sub="Available to offset balance"
            />
            <MetricCard
              label="Expected collection"
              value={fmtMoney(report.totals.expectedCollection)}
              accent="green"
              sub="balance − unapplied credit (clamped at 0)"
            />
          </div>

          <Card>
            <CardHeader>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Per customer</span>
              <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
                Click a row to see invoices + credits.
              </span>
            </CardHeader>
            <ReceivablesTable
              rows={report.rows}
              expanded={expanded}
              setExpanded={setExpanded}
            />
          </Card>
        </>
      )}
    </>
  );
}

// ─── CSV export ─────────────────────────────────────────────────────────────

function exportCsv(report: ReceivablesReport): void {
  const rows: Array<Array<unknown>> = [[
    'account_key', 'account_name', 'chargeover_customer_id', 'customer_display_name',
    'total', 'payments', 'credits_applied', 'writeoffs', 'balance',
    'available_credit', 'expected_collection', 'invoice_count', 'credit_count',
  ]];
  for (const r of report.rows) {
    rows.push([
      r.tempoAccountId ?? '', r.accountName ?? '',
      r.chargeoverCustomerId ?? '', r.customerDisplayName ?? '',
      r.total.toFixed(2), r.payments.toFixed(2), r.credits.toFixed(2),
      r.writeoffs.toFixed(2), r.balance.toFixed(2),
      r.availableCredit.toFixed(2), r.expectedCollection.toFixed(2),
      r.invoiceCount, r.creditCount,
    ]);
  }
  downloadCsv(`receivables_${report.monthKey}.csv`, rows);
}

// ─── Table + drill-down ─────────────────────────────────────────────────────

function ReceivablesTable({
  rows, expanded, setExpanded,
}: {
  rows: ReceivablesRow[];
  expanded: string | null;
  setExpanded: (next: string | null) => void;
}) {
  const { sorted, sort } = useTableSort(rows, {
    customer: (r: ReceivablesRow) => r.customerDisplayName,
    total:    (r: ReceivablesRow) => r.total,
    payments: (r: ReceivablesRow) => r.payments,
    balance:  (r: ReceivablesRow) => r.balance,
    credit:   (r: ReceivablesRow) => r.availableCredit,
    expected: (r: ReceivablesRow) => r.expectedCollection,
    invoices: (r: ReceivablesRow) => r.invoiceCount,
  }, { key: 'expected', dir: 'desc' });
  if (rows.length === 0) {
    return <EmptyState message="No invoices due in this month and no unapplied credits." />;
  }
  return (
    <Table>
      <thead>
        <tr>
          <SortableTh columnKey="customer" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Customer</SortableTh>
          <SortableTh columnKey="invoices" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Invoices</SortableTh>
          <SortableTh columnKey="total"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Total</SortableTh>
          <SortableTh columnKey="payments" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Paid</SortableTh>
          <SortableTh columnKey="balance"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Balance</SortableTh>
          <SortableTh columnKey="credit"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Unapplied credit</SortableTh>
          <SortableTh columnKey="expected" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Expected</SortableTh>
          <Th> </Th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => {
          const key = r.chargeoverCustomerId ?? r.tempoAccountId ?? 'unmapped';
          const isOpen = expanded === key;
          return (
            <>
              <Tr key={key} onClick={() => setExpanded(isOpen ? null : key)} selected={isOpen}>
                <Td bold>
                  {r.customerDisplayName ?? '—'}
                  {r.accountName && r.accountName !== r.customerDisplayName && (
                    <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: 10, marginLeft: 6 }}>
                      {r.accountName}
                    </span>
                  )}
                </Td>
                <Td right>{r.invoiceCount}</Td>
                <Td right>{fmtMoney(r.total)}</Td>
                <Td right muted>{fmtMoney(r.payments)}</Td>
                <Td right style={{ color: r.balance > 0 ? 'var(--color-amber)' : undefined }}>
                  {fmtMoney(r.balance)}
                </Td>
                <Td right muted>{r.availableCredit > 0 ? fmtMoney(r.availableCredit) : '—'}</Td>
                <Td right bold style={{ color: r.expectedCollection > 0 ? 'var(--color-green)' : 'var(--color-text-muted)' }}>
                  {fmtMoney(r.expectedCollection)}
                </Td>
                <Td muted style={{ fontSize: 10 }}>{isOpen ? 'Hide ↑' : 'Details ↓'}</Td>
              </Tr>
              {isOpen && (
                <tr key={`${key}-detail`}>
                  <td colSpan={8} style={{ padding: '8px 16px 14px', background: 'var(--color-bg)' }}>
                    <Drilldown invoices={r.invoices} credits={r.credits_detail} />
                  </td>
                </tr>
              )}
            </>
          );
        })}
      </tbody>
    </Table>
  );
}

function Drilldown({
  invoices, credits,
}: {
  invoices: ReceivablesInvoice[];
  credits: ReceivablesCredit[];
}) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {invoices.length > 0 && (
        <div>
          <div style={{
            fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)',
            textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4,
          }}>
            Invoices due this month ({invoices.length})
          </div>
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: 'var(--color-text-muted)' }}>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>Invoice</th>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>Due</th>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Total</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Paid</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Credits</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Write-offs</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.invoiceId}>
                  <td style={{ padding: '4px 8px' }}>
                    <InvoiceLink
                      invoiceId={inv.invoiceId}
                      display={inv.invoiceNumber ?? inv.invoiceId}
                    />
                    {inv.externalKey && (
                      <div style={{ fontSize: 10, color: 'var(--color-text-hint)' }}>{inv.externalKey}</div>
                    )}
                  </td>
                  <td style={{ padding: '4px 8px' }}>{inv.dueDate ?? '—'}</td>
                  <td style={{ padding: '4px 8px' }}>{inv.status ?? '—'}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{fmtMoney(inv.total)}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--color-text-muted)' }}>{fmtMoney(inv.payments)}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--color-text-muted)' }}>{fmtMoney(inv.credits)}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--color-text-muted)' }}>{fmtMoney(inv.writeoffs)}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 500, color: inv.balance > 0 ? 'var(--color-amber)' : undefined }}>
                    {fmtMoney(inv.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {credits.length > 0 && (
        <div>
          <div style={{
            fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)',
            textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4,
          }}>
            Unapplied credits ({credits.length})
          </div>
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: 'var(--color-text-muted)' }}>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>Transaction</th>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>Date</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Original</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Applied</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Unapplied</th>
              </tr>
            </thead>
            <tbody>
              {credits.map((c) => (
                <tr key={c.transactionId}>
                  <td style={{ padding: '4px 8px' }}>{c.transactionId}</td>
                  <td style={{ padding: '4px 8px' }}>{c.transactionDate ?? '—'}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{fmtMoney(c.amount)}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--color-text-muted)' }}>{fmtMoney(c.applied)}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 500, color: c.unapplied > 0 ? 'var(--color-green)' : undefined }}>
                    {fmtMoney(c.unapplied)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {invoices.length === 0 && credits.length === 0 && (
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
          No invoice or credit detail.
        </span>
      )}
    </div>
  );
}
